import React from 'react';

import { Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import ClassSessionFilters from './ClassSessionFilters';
import ClassSessionsContainer from './ClassSessionsContainer';
import ClassSessionsDateRange from './ClassSessionsDateRange';
import LoadingTableRows from './LoadingTableRows';
import { type Direction, DirectionOption, type SortBy, SortOption, Status } from './types';
import { useTablePagination, TablePagination, PaginationContainer } from '../../../../components/TablePagination';
import StyledFormTitle from '../../styles/form-title';
import { StyledTableCell } from '../../styles/table-styles';

const StyledTable = styled(Table)`
    margin-bottom: 2rem;
`;

const Title = styled(StyledFormTitle)`
    margin-bottom: 1rem;
`;

const ClassSessionLink = styled(Link)`
    color: ${(props) => props.theme.colors.primary.main};
`;

const ClassSessionsTable: React.FC = () => {
    const { filteredClassSessions, statuses, sortBy, setSortBy, sortDirection, setSortDirection } =
        useContainer(ClassSessionsContainer);
    const { t } = useTranslation('portal');
    const { path } = useRouteMatch();

    const handleSortSelection = ({ direction, _sortBy }: { direction: Direction; _sortBy: SortBy }) => {
        if (sortBy !== _sortBy) {
            setSortDirection(DirectionOption.ASCENDING);
            setSortBy(_sortBy);
            return;
        }

        setSortBy(_sortBy);

        if (!direction) setSortDirection(DirectionOption.ASCENDING);
        if (direction === DirectionOption.ASCENDING) setSortDirection(DirectionOption.DESCENDING);
        if (direction === DirectionOption.DESCENDING) setSortDirection(undefined);
    };

    const { onRowsPerPageChange, paginatedValues, onPageChange, rowsPerPage, setPage, count, page } =
        useTablePagination(filteredClassSessions);

    return (
        <>
            <Title>{t('classSessions.title')}</Title>
            <ClassSessionFilters setPage={setPage} />
            <StyledTable>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Tooltip arrow title={t('classSessions.sort')}>
                                <TableSortLabel
                                    active={sortBy === SortOption.NAME && !!sortDirection}
                                    direction={sortDirection}
                                    onClick={() =>
                                        handleSortSelection({ direction: sortDirection, _sortBy: SortOption.NAME })
                                    }
                                >
                                    {t('classSessions.name-with-count', {
                                        classSessionCount: filteredClassSessions.length
                                    })}
                                </TableSortLabel>
                            </Tooltip>
                        </TableCell>
                        <TableCell>
                            <Tooltip arrow title={t('classSessions.sort')}>
                                <TableSortLabel
                                    active={sortBy === SortOption.DATES && !!sortDirection}
                                    direction={sortDirection}
                                    onClick={() =>
                                        handleSortSelection({ direction: sortDirection, _sortBy: SortOption.DATES })
                                    }
                                >
                                    {t('classSessions.dates')}
                                </TableSortLabel>
                            </Tooltip>
                        </TableCell>
                        <TableCell>{t('classSessions.classSession-link')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {statuses.fetchClassSessions === Status.PENDING ? <LoadingTableRows /> : null}
                    {paginatedValues.map((classSessionRecord) => (
                        <TableRow key={classSessionRecord.class_session_uuid}>
                            <StyledTableCell>
                                <ClassSessionLink
                                    to={`${path}/${
                                        classSessionRecord.class_session_uuid
                                    }?classSessionName=${encodeURIComponent(classSessionRecord.class_session_topic!)}`}
                                >
                                    {classSessionRecord.class_session_topic}
                                </ClassSessionLink>
                            </StyledTableCell>

                            <StyledTableCell>
                                <ClassSessionsDateRange
                                    start={classSessionRecord.starting_at}
                                    end={classSessionRecord.ending_at}
                                />
                            </StyledTableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </StyledTable>
            <PaginationContainer>
                <TablePagination
                    count={count}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onRowsPerPageChange}
                />
            </PaginationContainer>
        </>
    );
};

export default ClassSessionsTable;
